/**
 * Suivi des demandes de devis.
 *
 * Deux vues sur le même jeu de données : une table, pour chercher et comparer,
 * et un kanban, pour faire avancer. Elles partagent les filtres et le même
 * cache — ce que l'une modifie, l'autre l'affiche aussitôt.
 *
 * Seule la période est envoyée à l'API ; statut, commune et tri sont appliqués
 * ici. Un filtre par statut côté serveur viderait le kanban de ses colonnes.
 */
import TableRowsIcon from "@mui/icons-material/TableRows";
import ViewKanbanIcon from "@mui/icons-material/ViewKanban";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Typography from "@mui/material/Typography";
import { useMemo, useState } from "react";
import {
  type Lead,
  type StatutLead,
  periodeDepuis,
  useLeads,
  useSuivreLead,
} from "../composants/LeadsDonnees";
import { FILTRES_VIDES, type Filtres, LeadsFiltres } from "../composants/LeadsFiltres";
import { LeadsKanban } from "../composants/LeadsKanban";
import { LeadsTable, TRI_PAR_DEFAUT, type Tri, trierLeads } from "../composants/LeadsTable";

type Vue = "table" | "kanban";

/**
 * Compare sans tenir compte des accents ni de la casse : « Menucourt » doit
 * répondre à « ménucourt » comme à « MENUCOURT ». Une commune se cherche à la
 * volée, entre deux appels, pas au caractère près.
 */
function sansAccent(valeur: string): string {
  return valeur
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase();
}

function filtrer(leads: Lead[], filtres: Filtres, vue: Vue): Lead[] {
  const commune = sansAccent(filtres.commune.trim());

  return leads.filter((lead) => {
    if (vue === "table" && filtres.statut !== "" && lead.statut !== filtres.statut) return false;
    if (commune !== "" && !sansAccent(lead.communeTexte).includes(commune)) return false;
    return true;
  });
}

export function Leads() {
  const [vue, setVue] = useState<Vue>("table");
  const [filtres, setFiltres] = useState<Filtres>(FILTRES_VIDES);
  const [tri, setTri] = useState<Tri>(TRI_PAR_DEFAUT);

  const periode = useMemo(
    () => (filtres.jours === null ? {} : periodeDepuis(filtres.jours)),
    [filtres.jours],
  );

  const requete = useLeads(periode);
  const suivi = useSuivreLead();

  const leads = requete.data?.leads ?? [];
  const visibles = useMemo(() => filtrer(leads, filtres, vue), [leads, filtres, vue]);
  const triees = useMemo(() => trierLeads(visibles, tri), [visibles, tri]);

  function deplacer(id: number, statut: StatutLead) {
    suivi.mutate({ id, statut });
  }

  return (
    <Stack spacing={3}>
      <Typography variant="body1" color="text.secondary">
        Les demandes reçues par le formulaire du site, de leur arrivée à l'affaire signée.
      </Typography>

      <Tabs
        value={vue}
        onChange={(_evenement, valeur: Vue) => setVue(valeur)}
        aria-label="Mode d'affichage des demandes"
      >
        <Tab value="table" icon={<TableRowsIcon />} iconPosition="start" label="Table" />
        <Tab value="kanban" icon={<ViewKanbanIcon />} iconPosition="start" label="Kanban" />
      </Tabs>

      <LeadsFiltres filtres={filtres} surChangement={setFiltres} avecStatut={vue === "table"} />

      {/* L'échec d'un changement de statut se dit ici : dans le kanban, la
          carte est déjà revenue à sa colonne d'origine, et ce retour silencieux
          passerait pour un glisser mal visé. */}
      {suivi.isError && (
        <Alert severity="error" onClose={() => suivi.reset()}>
          Le statut n'a pas pu être enregistré. {suivi.error.messageGlobal}
        </Alert>
      )}

      {requete.isPending && <Skeleton variant="rounded" height={280} />}

      {requete.isError && (
        <Alert severity="error">
          Les demandes n'ont pas pu être chargées. {requete.error.message}
        </Alert>
      )}

      {requete.isSuccess && (
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }} aria-live="polite">
            {visibles.length === 0
              ? "Aucune demande"
              : `${visibles.length} demande${visibles.length > 1 ? "s" : ""}`}
            {visibles.length !== leads.length ? ` sur ${leads.length}` : ""}
          </Typography>

          {vue === "table" ? (
            <LeadsTable leads={triees} tri={tri} surTri={setTri} />
          ) : (
            <LeadsKanban leads={visibles} surDeplacement={deplacer} enCours={suivi.isPending} />
          )}
        </Box>
      )}
    </Stack>
  );
}
